<script setup>
import { format, fromUnixTime } from 'date-fns';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  chat: {
    type: Object,
    default: () => ({}),
  },
});

const { SLA_MISS_TYPES } = wootConstants;
const now = Math.floor(Date.now() / 1000);

const formatDate = timestamp =>
  format(fromUnixTime(timestamp), 'MMM dd, yyyy, hh:mm a');

/**
 * Formats the SLA time in a human-readable format.
 * @param {number} seconds - The time in seconds.
 * @returns {string} A formatted string representing the time.
 */
const formatSLATime = seconds => {
  const units = {
    y: 31536000, // 60 * 60 * 24 * 365
    mo: 2592000, // 60 * 60 * 24 * 30
    d: 86400, // 60 * 60 * 24
    h: 3600, // 60 * 60
    m: 60,
  };

  if (seconds < 60) {
    return '1m';
  }

  // we will only show two parts, two max granularity's, h-m, y-d, d-h, m, but no seconds
  const parts = [];

  Object.keys(units).forEach(unit => {
    const value = Math.floor(seconds / units[unit]);
    if (seconds < 60 && parts.length > 0) return;
    if (parts.length === 2) return;
    if (value > 0) {
      parts.push(value + unit);
      seconds -= value * units[unit];
    }
  });

  return parts.join(' ');
};

// The header icon derives its overdue from the live chat state via
// evaluateSLAStatus. Mirror the same anchors here so both surfaces always
// show the same number while a metric is still running; once a metric is
// settled, fall back to the delay recorded on the SLA event.
const liveOverdueSeconds = item => {
  const {
    sla_first_response_time_threshold: frtThreshold,
    sla_next_response_time_threshold: nrtThreshold,
    sla_resolution_time_threshold: rtThreshold,
    created_at: appliedAt,
  } = props.chat?.applied_sla || {};
  const {
    first_reply_created_at: firstReplyAt,
    waiting_since: waitingSince,
    status,
  } = props.chat || {};

  switch (item.event_type) {
    case SLA_MISS_TYPES.FRT:
      if (!appliedAt || !frtThreshold) return null;
      // solved: freeze at the exact delay of the first reply
      if (firstReplyAt) return firstReplyAt - (appliedAt + frtThreshold);
      return now - (appliedAt + frtThreshold);
    case SLA_MISS_TYPES.NRT:
      if (!nrtThreshold || !waitingSince) return null;
      return now - (waitingSince + nrtThreshold);
    case SLA_MISS_TYPES.RT:
      if (!appliedAt || !rtThreshold || status === 'resolved') return null;
      return now - (appliedAt + rtThreshold);
    default:
      return null;
  }
};

const overdueSeconds = item => {
  const live = liveOverdueSeconds(item);
  if (live !== null && live > 0) return live;
  return item.delay_duration || 0;
};
</script>
<template>
  <div class="flex justify-between w-full">
    <span
      class="text-sm sticky top-0 h-fit font-normal tracking-[-0.6%] min-w-[140px] truncate text-slate-600 dark:text-slate-200"
    >
      {{ label }}
    </span>
    <div class="flex flex-col w-full gap-2">
      <span
        v-for="item in items"
        :key="item.id"
        class="text-sm font-normal text-slate-900 dark:text-slate-25 text-right tabular-nums"
      >
        <span class="flex justify-between">
          {{ formatDate(item.created_at) }}
          <span v-if="overdueSeconds(item) > 0" class="ms-1 whitespace-nowrap">
            ({{ formatSLATime(overdueSeconds(item)) }}
            {{ $t('SLA.EVENTS.OVERDUE') }})
          </span>
        </span>
      </span>
      <slot name="showMore" />
    </div>
  </div>
</template>
